import { TransportError } from "@econ-v1/domain";
import { canonicalHttpsOrigin, normalizeHttpsOrigin } from "./http-origin.js";
const MAX_LAN_ORIGINS = 8;
/** Mirrors `MAX_STUN_URLS` in `core/adapters-network/src/webrtc_signal/stun.rs`. */
const MAX_STUN_URLS = 8;
const STUN_URL = /^stun:\S+$/u;
const SHA256_HEX = /^[0-9a-fA-F]{64}$/;
/** Discovers only HTTPS browser transport candidates from the authenticated home node. */
export class ClientTransportDiscovery {
    #fetch;
    constructor(options = {}) {
        this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    }
    async establishTrust(baseUrl, headers, signal) {
        const data = await this.#getCandidates(baseUrl, headers, signal);
        if (!isNodeId(data.node_id))
            throw unavailable("Home node transport identity was invalid");
        return candidatesFor(data, data.node_id);
    }
    async refresh(baseUrl, expectedHomeNodeId, headers, signal) {
        if (!isNodeId(expectedHomeNodeId))
            throw unavailable("Home node transport identity was invalid");
        const data = await this.#getCandidates(baseUrl, headers, signal);
        if (data.node_id !== expectedHomeNodeId) {
            throw unavailable("Home node transport identity did not match");
        }
        const candidates = candidatesFor(data, expectedHomeNodeId);
        return {
            lan: candidates.lan,
            stunUrls: candidates.stunUrls,
            wan: candidates.wan,
            ...(candidates.webtransport === undefined ? {} : { webtransport: candidates.webtransport }),
        };
    }
    async probeLan(candidate, headers, signal) {
        if (candidate.path !== "lan-direct" || !isNodeId(candidate.id)) {
            throw unavailable("LAN candidate identity was invalid");
        }
        const origin = canonicalHttpsOrigin(candidate.address);
        const proof = await this.#get(origin, "/.well-known/client-node-origin", {}, parseOriginProof, signal);
        if (proof.node_id !== candidate.id || proof.origin !== origin) {
            throw unavailable("LAN candidate did not prove the expected home node");
        }
        const refreshed = await this.refresh(origin, candidate.id, headers, signal);
        if (!refreshed.lan.some((item) => item.address === origin)) {
            throw unavailable("LAN candidate was not advertised by the home node");
        }
        return { address: origin, id: candidate.id, path: "lan-direct" };
    }
    async #getCandidates(baseUrl, headers, signal) {
        return await this.#get(normalizeHttpsOrigin(baseUrl), "/api/v2/client-transport/candidates", headers, parseCandidates, signal);
    }
    async #get(origin, path, headers, parse, signal) {
        let response;
        try {
            response = await this.#fetch(new Request(new URL(path, origin), {
                headers: new Headers(headers),
                method: "GET",
                ...(signal === undefined ? {} : { signal }),
            }));
        }
        catch {
            throw new TransportError({ code: "offline", message: "Home node transport discovery is unavailable" });
        }
        if (!response.ok)
            throw unavailable("Home node transport discovery was rejected");
        let value;
        try {
            value = await response.json();
        }
        catch {
            throw unavailable("Home node transport discovery returned invalid JSON");
        }
        const envelope = parseApiResponse(value);
        return parse(envelope.data);
    }
}
function candidatesFor(data, homeNodeId) {
    const webtransport = data.webtransport === undefined
        ? undefined
        : webTransportCandidateFor(data.webtransport, homeNodeId);
    return {
        homeNodeId,
        lan: normalizeLanOrigins(data.lan_origins, homeNodeId),
        stunUrls: data.stun_urls ?? [],
        wan: { address: canonicalOrigin(data.wan_origin), id: homeNodeId, path: "wan-direct" },
        ...(webtransport === undefined ? {} : { webtransport }),
    };
}
function webTransportCandidateFor(candidate, homeNodeId) {
    return {
        address: canonicalOrigin(candidate.origin),
        certificateHashes: {
            algorithm: "sha-256",
            current: candidate.certificate_hashes.current.toLowerCase(),
            next: candidate.certificate_hashes.next.toLowerCase(),
        },
        id: homeNodeId,
        path: "lan-webtransport",
    };
}
function normalizeLanOrigins(origins, homeNodeId) {
    if (!Array.isArray(origins))
        throw unavailable("Home node LAN candidates were invalid");
    const addresses = new Set();
    for (const origin of origins) {
        if (typeof origin !== "string")
            throw unavailable("Home node LAN candidates were invalid");
        const address = canonicalOrigin(origin);
        if (addresses.has(address))
            continue;
        addresses.add(address);
        if (addresses.size === MAX_LAN_ORIGINS)
            break;
    }
    return [...addresses].map((address) => ({ address, id: homeNodeId, path: "lan-direct" }));
}
/** Exported for `./node-address-discovery.ts`, which unwraps the same envelope shape. */
export function parseApiResponse(value) {
    if (!isExactRecord(value, ["data", "success", "timestamp"]) ||
        value.success !== true || typeof value.timestamp !== "string") {
        throw unavailable("Home node transport discovery returned an invalid response");
    }
    return { data: value.data, success: true, timestamp: value.timestamp };
}
const ORIGIN_PROOF_KEYS = ["node_id", "origin", "version"];
/** Exported for `./node-address-discovery.ts`, which reuses this exact validation. */
export function parseOriginProof(value) {
    const isRecord = typeof value === "object" && value !== null && !Array.isArray(value);
    const hasTrustModeKey = isRecord && Object.hasOwn(value, "trust_mode");
    const expectedKeys = hasTrustModeKey
        ? [...ORIGIN_PROOF_KEYS, "trust_mode"]
        : ORIGIN_PROOF_KEYS;
    if (!isExactRecord(value, expectedKeys) ||
        value.version !== 1 || !isNodeId(value.node_id) || typeof value.origin !== "string") {
        throw unavailable("Home node origin proof was invalid");
    }
    return {
        node_id: value.node_id,
        origin: canonicalOrigin(value.origin),
        trust_mode: value.trust_mode === "publicly-trusted" ? "publicly-trusted" : "self-signed",
        version: 1,
    };
}
const CANDIDATE_RESPONSE_KEYS = ["lan_origins", "node_id", "version", "wan_origin"];
function parseCandidates(value) {
    const isRecord = typeof value === "object" && value !== null && !Array.isArray(value);
    const hasWebtransportKey = isRecord && Object.hasOwn(value, "webtransport");
    const hasStunUrlsKey = isRecord && Object.hasOwn(value, "stun_urls");
    const expectedKeys = [
        ...CANDIDATE_RESPONSE_KEYS,
        ...(hasWebtransportKey ? ["webtransport"] : []),
        ...(hasStunUrlsKey ? ["stun_urls"] : []),
    ];
    if (!isExactRecord(value, expectedKeys) ||
        value.version !== 1 || !isNodeId(value.node_id) || typeof value.wan_origin !== "string" ||
        !Array.isArray(value.lan_origins) || !value.lan_origins.every((origin) => typeof origin === "string")) {
        throw unavailable("Home node transport candidates were invalid");
    }
    const webtransport = hasWebtransportKey ? parseWebTransportCandidate(value.webtransport) : undefined;
    const stunUrls = hasStunUrlsKey ? parseStunUrls(value.stun_urls) : undefined;
    return {
        lan_origins: [...value.lan_origins],
        node_id: value.node_id,
        version: 1,
        wan_origin: canonicalOrigin(value.wan_origin),
        ...(webtransport === undefined ? {} : { webtransport }),
        ...(stunUrls === undefined ? {} : { stun_urls: stunUrls }),
    };
}
/**
 * Bounded, deduped, well-formed subset of the node's advertised `stun_urls`
 * (ticket #1865/#1866 review "FIX 4"). The node already qualifies/bounds
 * this list (`qualify_stun_urls` in `core/adapters-network/src/webrtc_signal/
 * stun.rs`) — this is defense-in-depth, not the primary validation, mirroring
 * `normalizeLanOrigins` below for the same reason.
 */
function parseStunUrls(value) {
    if (!Array.isArray(value) || !value.every((url) => typeof url === "string")) {
        throw unavailable("Home node STUN candidates were invalid");
    }
    const urls = [];
    const seen = new Set();
    for (const url of value) {
        if (!STUN_URL.test(url) || seen.has(url))
            continue;
        seen.add(url);
        urls.push(url);
        if (urls.length === MAX_STUN_URLS)
            break;
    }
    return urls;
}
function parseWebTransportCandidate(value) {
    if (!isExactRecord(value, ["certificate_hashes", "origin"]) || typeof value.origin !== "string") {
        throw unavailable("Home node WebTransport candidate was invalid");
    }
    const hashes = value.certificate_hashes;
    if (!isExactRecord(hashes, ["algorithm", "current", "next"]) ||
        hashes.algorithm !== "sha-256" ||
        !isSha256Hex(hashes.current) ||
        !isSha256Hex(hashes.next)) {
        throw unavailable("Home node WebTransport certificate hashes were invalid");
    }
    return {
        certificate_hashes: { algorithm: "sha-256", current: hashes.current, next: hashes.next },
        origin: canonicalOrigin(value.origin),
    };
}
function isSha256Hex(value) {
    return typeof value === "string" && SHA256_HEX.test(value);
}
function canonicalOrigin(value) {
    try {
        return canonicalHttpsOrigin(value);
    }
    catch {
        throw unavailable("Home node transport origin was invalid");
    }
}
function isExactRecord(value, keys) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const actual = Object.keys(value).sort();
    const expected = [...keys].sort();
    return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
function isNodeId(value) {
    return typeof value === "string" && value.trim() === value && value.length > 0;
}
function unavailable(message) {
    return new TransportError({ code: "unavailable", message });
}
//# sourceMappingURL=client-transport-discovery.js.map